import React, { useState, useEffect } from 'react';

const ConsultationHub = () => {
  const [activeTab, setActiveTab] = useState('instant');
  const [consultations, setConsultations] = useState([]);

  // Consultation types with competitive pricing
  const consultationTypes = {
    instant: {
      title: '⚡ Instant Consultation',
      subtitle: 'Get medical advice within 5 minutes',
      price: 49,
      features: [
        'Immediate response (5 min avg)',
        'Chat or voice call',
        'Prescription if needed',
        'Follow-up included',
        '24/7 availability'
      ],
      doctors: [
        { name: 'Dr. Sarah Kim', specialty: 'Family Medicine', rating: 4.9, responseTime: '2 min', online: true },
        { name: 'Dr. Ahmed Hassan', specialty: 'Internal Medicine', rating: 4.8, responseTime: '3 min', online: true },
        { name: 'Dr. Maria Rodriguez', specialty: 'Pediatrics', rating: 4.9, responseTime: '1 min', online: true }
      ]
    },
    video: {
      title: '📹 Video Consultation',
      subtitle: 'Face-to-face consultation from anywhere',
      price: 75,
      features: [
        'HD video call',
        'Screen sharing for results',
        'Session recording',
        'Digital prescription',
        'Schedule flexibility'
      ],
      doctors: [
        { name: 'Dr. Jennifer Chen', specialty: 'Dermatology', rating: 4.9, nextSlot: '2:30 PM today', online: true },
        { name: 'Dr. Robert Johnson', specialty: 'Cardiology', rating: 4.8, nextSlot: '4:00 PM today', online: true },
        { name: 'Dr. Lisa Wang', specialty: 'Endocrinology', rating: 4.9, nextSlot: '9:00 AM tomorrow', online: false }
      ]
    },
    specialist: {
      title: '🩺 Specialist Consultation',
      subtitle: 'Expert consultation with top specialists',
      price: 150,
      features: [
        'Board-certified specialists',
        'Detailed medical review',
        'Treatment plan included',
        'Lab/imaging interpretation',
        'Care coordination'
      ],
      doctors: [
        { name: 'Dr. Michael Thompson', specialty: 'Oncology', rating: 5.0, waitTime: '24 hours', online: false },
        { name: 'Dr. Patricia Davis', specialty: 'Neurology', rating: 4.9, waitTime: '12 hours', online: true },
        { name: 'Dr. James Wilson', specialty: 'Orthopedics', rating: 4.8, waitTime: '6 hours', online: true }
      ]
    },
    mental: {
      title: '🧠 Mental Health',
      subtitle: 'Therapy and psychiatric consultations',
      price: 120,
      features: [
        'Licensed therapists/psychiatrists',
        'Confidential sessions',
        'Therapy notes included',
        'Medication management',
        'Crisis support available'
      ],
      doctors: [
        { name: 'Dr. Emily Foster', specialty: 'Clinical Psychology', rating: 4.9, nextSlot: '1:00 PM today', online: true },
        { name: 'Dr. David Park', specialty: 'Psychiatry', rating: 4.8, nextSlot: '11:00 AM tomorrow', online: false },
        { name: 'Dr. Rachel Green', specialty: 'Family Therapy', rating: 5.0, nextSlot: '6:00 PM today', online: true }
      ]
    }
  };

  // Quick consultation categories
  const quickConsultations = [
    { 
      category: 'Urgent Care',
      icon: '🚨',
      description: 'Immediate medical attention needed',
      examples: ['Severe pain', 'High fever', 'Difficulty breathing', 'Injury'],
      price: 89,
      responseTime: '< 2 minutes'
    },
    {
      category: 'Prescription Refill',
      icon: '💊',
      description: 'Renew your current medications',
      examples: ['Chronic conditions', 'Regular medications', 'Birth control', 'Insulin'],
      price: 29,
      responseTime: '< 10 minutes'
    },
    {
      category: 'Symptom Check',
      icon: '🩺',
      description: 'Evaluate symptoms and get guidance',
      examples: ['Cold/flu symptoms', 'Skin conditions', 'Digestive issues', 'Headaches'],
      price: 39,
      responseTime: '< 5 minutes'
    },
    {
      category: 'Lab Results Review',
      icon: '📋',
      description: 'Expert interpretation of test results',
      examples: ['Blood work', 'Imaging results', 'Biopsy reports', 'Genetic tests'],
      price: 59,
      responseTime: '< 30 minutes'
    }
  ];

  // Render consultation type tab
  const ConsultationTypeTab = ({ type, data }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-900">{data.title}</h2>
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-600">${data.price}</div>
            <div className="text-sm text-gray-500">per consultation</div>
          </div>
        </div>
        <p className="text-gray-600">{data.subtitle}</p>
      </div>

      {/* Features */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">What's Included:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {data.features.map((feature, index) => (
            <div key={index} className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-gray-700">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Available Doctors */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">Available Doctors:</h3>
        <div className="space-y-3">
          {data.doctors.map((doctor, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold">
                    {doctor.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">{doctor.name}</div>
                  <div className="text-sm text-gray-500">{doctor.specialty}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center space-x-1 mb-1">
                  <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-sm font-medium">{doctor.rating}</span>
                </div>
                <div className="text-xs text-gray-500">
                  {doctor.responseTime && `Avg: ${doctor.responseTime}`}
                  {doctor.nextSlot && `Next: ${doctor.nextSlot}`}
                  {doctor.waitTime && `Wait: ${doctor.waitTime}`}
                </div>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  doctor.online 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full mr-1 ${
                    doctor.online ? 'bg-green-400' : 'bg-gray-400'
                  }`} />
                  {doctor.online ? 'Online' : 'Busy'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Start Consultation Button */}
      <button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-lg font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105">
        Start {data.title.replace(/[^\w\s]/gi, '')} - ${data.price}
      </button>
    </div>
  );

  // Quick consultation cards
  const QuickConsultationCard = ({ consultation }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="text-3xl">{consultation.icon}</div>
          <div>
            <h3 className="font-semibold text-gray-900">{consultation.category}</h3>
            <p className="text-sm text-gray-600">{consultation.description}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-blue-600">${consultation.price}</div>
          <div className="text-xs text-green-600">{consultation.responseTime}</div>
        </div>
      </div>
      
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Common examples:</h4>
        <div className="flex flex-wrap gap-1">
          {consultation.examples.map((example, index) => (
            <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
              {example}
            </span>
          ))}
        </div>
      </div>

      <button className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors">
        Start Consultation
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Medical Consultations</h1>
            <p className="text-xl text-blue-100 mb-6">
              Connect with healthcare professionals instantly - anytime, anywhere
            </p>
            
            {/* Stats */}
            <div className="flex justify-center space-x-8 text-center">
              <div>
                <div className="text-2xl font-bold">2.5 min</div>
                <div className="text-sm text-blue-100">Average response time</div>
              </div>
              <div>
                <div className="text-2xl font-bold">98.5%</div>
                <div className="text-sm text-blue-100">User satisfaction</div>
              </div>
              <div>
                <div className="text-2xl font-bold">24/7</div>
                <div className="text-sm text-blue-100">Availability</div>
              </div>
              <div>
                <div className="text-2xl font-bold">500+</div>
                <div className="text-sm text-blue-100">Licensed doctors</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Quick Consultations */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Quick Consultations</h2>
            <p className="text-gray-600">Get help fast for common health concerns</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickConsultations.map((consultation, index) => (
              <QuickConsultationCard key={index} consultation={consultation} />
            ))}
          </div>
        </div>

        {/* Consultation Types Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {Object.entries(consultationTypes).map(([key, data]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {data.title}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Active Tab Content */}
        <ConsultationTypeTab type={activeTab} data={consultationTypes[activeTab]} />

        {/* Trust Indicators */}
        <div className="mt-12 bg-white rounded-xl border border-gray-200 p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Why Choose EnayaHub Consultations?</h2>
            <p className="text-gray-600">Superior care that outperforms the competition</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Fastest Response Times</h3>
              <p className="text-gray-600">Average 2.5 minutes vs competitors' 15+ minutes</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Board-Certified Doctors</h3>
              <p className="text-gray-600">Only licensed physicians from top medical schools</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">HIPAA Compliant</h3>
              <p className="text-gray-600">Your health information is completely secure and private</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationHub;